
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigationItems, NavigationItem } from "@/hooks/useNavigationItems";
import { NavItem, NavItemChild } from "./NavItem";
import { NavSeparator } from "./NavSeparator";
import { NavCategory } from "./NavCategory";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileNavigation } from "./MobileNavigation";

interface DashboardSidebarProps {
  isOpen: boolean;
  userRole?: string;
}

export const DashboardSidebar = ({ isOpen, userRole }: DashboardSidebarProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const isMobile = useIsMobile();
  
  const { 
    categories, 
    categorizedItems, 
    isActive,
    isExpanded,
    toggleExpanded,
    navigate: navigateTo, 
    preload: preloadView 
  } = useNavigationItems(userRole);
  
  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  // Render mobile navigation if on mobile
  if (isMobile) {
    return <MobileNavigation userRole={userRole} />;
  }

  return (
    <motion.div
      initial={{ width: isOpen ? 256 : 80 }}
      animate={{ width: isOpen ? (collapsed ? 80 : 256) : 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={cn(
        "fixed left-0 top-0 h-screen bg-white border-r border-slate-200 z-20 flex flex-col shadow-sm",
        !isOpen && "hidden md:flex"
      )}
    >
      {/* Sidebar Header */}
      <div className="h-16 border-b border-slate-200 flex items-center gap-3 px-4">
        {!collapsed && (
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center text-white font-bold text-lg">
              A
            </div>
            <h2 className="text-lg font-semibold text-slate-900 truncate">
              App Dashboard
            </h2>
          </div>
        )}
        {collapsed && (
          <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center text-white font-bold text-lg mx-auto">
            A
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleToggleCollapse}
          className={cn("ml-auto", collapsed && "mx-auto")}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </Button>
      </div>
      
      <div className="flex-1 py-4 overflow-y-auto scrollbar-hide">
        {categories.map((category, index) => (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
          >
            {index > 0 && <NavSeparator />}
            <NavCategory label={category} collapsed={collapsed} />
            {categorizedItems[category].map((item) => (
              <div key={item.id}>
                <NavItem
                  item={item}
                  active={isActive(item)}
                  onClick={() => {
                    if (!item.children?.length) {
                      navigateTo(item.path);
                    }
                  }}
                  onToggleExpand={() => toggleExpanded(item.id)}
                  onHover={() => preloadView(item.path)}
                  collapsed={collapsed}
                  isExpanded={isExpanded(item.id)}
                />
                
                {item.children && item.children.length > 0 && !collapsed && (
                  <AnimatePresence>
                    {isExpanded(item.id) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        {item.children.map((child) => (
                          <NavItemChild
                            key={child.id}
                            item={child}
                            active={isActive(child)}
                            onClick={() => navigateTo(child.path)}
                            onHover={() => preloadView(child.path)}
                            collapsed={collapsed}
                          />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};
